import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { IAssistantTable } from '../assistant-markdown.pipe';

/**
 * Tabella di una risposta dell'assistente. Nel fumetto (≈300px) un'anteprima delle prime PREVIEW_ROWS righe, solo se
 * leggibile: celle corte (≤ PREVIEW_MAX_CELL caratteri) e nessuna colonna oltre la larghezza del fumetto (misurata dopo
 * il render). Altrimenti una scheda con righe/colonne e il pulsante. Tutta la tabella in un dialog largo con
 * intestazione e prima colonna fisse e copia per Excel (TSV).
 */
@Component({
	selector: 'assistant-table',
	templateUrl: './assistant-table.component.html',
	styleUrls: ['./assistant-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AssistantTableComponent implements OnInit, AfterViewInit{

	static readonly PREVIEW_ROWS = 5;
	static readonly PREVIEW_MAX_CELL = 60;

	@Input({ required: true }) table!: IAssistantTable;

	@ViewChild('scroll') scroll?: ElementRef<HTMLElement>;

	/** false = scheda al posto dell'anteprima */
	fits = true;

	/** chiave i18n con {{count}}: fuori dal template, dove le graffe chiuderebbero l'interpolazione */
	readonly allRowsLabel = 'Vedi tutte le {{count}} righe';

	constructor(private dialog: MatDialog, private changeDetector: ChangeDetectorRef){}

	ngOnInit(): void{
		const cells = [this.table.plain.header, ...this.table.plain.rows].flat();
		this.fits = cells.every(cell => cell.length <= AssistantTableComponent.PREVIEW_MAX_CELL);
	}

	ngAfterViewInit(): void{
		const element = this.scroll?.nativeElement;
		if(this.fits && element && element.scrollWidth > element.clientWidth + 1){
			this.fits = false;
			this.changeDetector.detectChanges();
		}
	}

	get sizeLabel(): string{
		return this.table.rows.length === 1 ? 'Tabella con 1 riga e {{columns}} colonne' : 'Tabella con {{rows}} righe e {{columns}} colonne';
	}

	/** "Prezzo speciale, Date di viaggio 2027, 1–2 persone…" per la scheda */
	get columnNames(): string{
		return this.table.plain.header.filter(Boolean).join(', ');
	}

	get previewRows(): string[][]{
		return this.table.rows.slice(0, AssistantTableComponent.PREVIEW_ROWS);
	}

	get hiddenRows(): number{
		return Math.max(0, this.table.rows.length - AssistantTableComponent.PREVIEW_ROWS);
	}

	open(): void{
		this.dialog.open(AssistantTableDialogComponent, {
			data: this.table,
			width: '960px',
			maxWidth: '94vw',
			maxHeight: '86vh',
			autoFocus: 'dialog',
			panelClass: 'assistant-table-dialog-panel'
		});
	}
}

@Component({
	selector: 'assistant-table-dialog',
	templateUrl: './assistant-table-dialog.component.html',
	styleUrls: ['./assistant-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AssistantTableDialogComponent{

	copied = false;

	constructor(@Inject(MAT_DIALOG_DATA) public table: IAssistantTable, private changeDetector: ChangeDetectorRef){}

	/** TSV: incollato in Excel o Fogli Google mantiene righe e colonne */
	copy(): void{
		const lines = [this.table.plain.header, ...this.table.plain.rows].map(row => row.map(cell => cell.replace(/[\t\n]+/g, ' ')).join('\t'));
		navigator.clipboard?.writeText(lines.join('\n')).then(() => {
			this.copied = true;
			this.changeDetector.markForCheck();
			setTimeout(() => {
				this.copied = false;
				this.changeDetector.markForCheck();
			}, 2000);
		});
	}
}
